import type {
  Currency,
  Language,
  Region,
} from '@whoop/i18n/types/internationalization';
import type { ApplicationScheme } from 'services/generated/growth-content-service/models/ApplicationScheme';
import { i18n } from '@whoop/i18n/lang/client';
import { formatPriceWithSiteValues } from '@whoop/i18n';
import type {
  PromoCode,
  PromoCodeApplicationScheme,
  PromoInfo,
  Referrer,
} from '../types';

export const PROMO_CODE_REGEX = /[a-zA-Z0-9]*/;

export const getMembershipTypeApplicationSchemeForCurrency = (
  schemes: PromoCodeApplicationScheme[] | ApplicationScheme[],
  currency: Currency,
): PromoCodeApplicationScheme | ApplicationScheme | undefined => {
  const schemeForCurrency = schemes.find(
    (scheme) =>
      scheme.currency === currency && scheme.application_type === 'membership',
  );
  if (schemeForCurrency) return schemeForCurrency;
  return schemes.find(
    (scheme) => !scheme.currency && scheme.application_type === 'membership',
  );
};

export const isMonthOffPromoCode = (promoCode?: PromoCode): boolean => {
  if (!promoCode?.application_schemes) return false;
  return Boolean(
    promoCode.application_schemes.find(
      (scheme) => scheme.promo_type === 'month_off',
    ),
  );
};

export const getReferrer = (promoInfo?: PromoInfo): Referrer | undefined => {
  if (promoInfo?.referral) {
    return promoInfo.referral.referrer;
  }
  if (promoInfo?.affiliate) {
    return {
      first_name:
        promoInfo.affiliate.name || i18n.t('joinLandingPage:yourFriend'),
      avatar_url: promoInfo.affiliate.avatar_url,
    };
  }
};

export const getMembershipPromoDisclaimerByCurrency = (
  applicationSchemas: ApplicationScheme[],
  currency: Currency,
): ApplicationScheme | undefined => {
  const applicationScheme = getMembershipTypeApplicationSchemeForCurrency(
    applicationSchemas,
    currency,
  );
  if (!applicationScheme) return;
  return applicationScheme;
};

/**
 * This grabs some of the copy to let the user know that a RAF/Affiliate promo has been applied throughout the flow
 * @param promoInfo {JoinFlowContent} - The promo info object
 * @param referrer {Referrer} - The referrer object
 * @param currency {Currency} - The currency string
 * @param language {Language} - The language string
 * @returns
 */
export const getPromoDisclaimer = ({
  promo,
  referrer,
  currency,
  language,
  region,
  location = 'primary',
}: {
  promo: ApplicationScheme | undefined;
  referrer: Referrer;
  currency: Currency;
  language: Language;
  region: Region;
  location: 'primary' | 'secondary';
}): string => {
  const { first_name: firstName } = referrer;
  const copyLocations = {
    primary: {
      month_off: 'joinLandingPage:referral.gave-you-one-month',
      percent_off: 'joinLandingPage:referral.gave-you-percentage-off',
      amount_off: 'joinLandingPage:referral.gave-you-amount-off',
    },
    secondary: {
      month_off: 'orderPage:oneMonthCallout',
      percent_off: 'orderPage:percentCallout',
      amount_off: 'orderPage:amountCallout',
    },
  };

  const copyLocation = copyLocations[location];

  if (!promo) return '';

  if (promo.application_type === 'membership') {
    switch (promo.promo_type) {
      case 'month_off':
        return i18n.t(copyLocation.month_off, {
          name: firstName,
        });
      case 'percent_off':
        return i18n.t(copyLocation.percent_off, {
          name: firstName,
          percentage: promo.amount,
        });
      case 'amount_off':
        return i18n.t(copyLocation.amount_off, {
          name: firstName,
          amount: formatPriceWithSiteValues(promo.amount, {
            currency,
            language,
            region,
          }),
        });
      default:
        return '';
    }
  }
  return '';
};
